/**
 * A scan, flattened to plan.
 *
 * This is the projection step between `scan-elements.ts` — which knows every
 * wall's size but nothing about where it sits — and anything that draws a floor
 * plan: the portal's interactive SVG today, and `floor-plan-svg.ts`'s report
 * renderer tomorrow, whose own header asks for exactly this ("When the true
 * corner angles are known, build `FloorPlanRoom.walls` from real positions
 * instead of going through this fallback"). It lives here rather than in the
 * portal so a plan drawn on screen and a plan printed into a survey report can
 * never disagree about where a wall is.
 *
 * THE FRAME. RoomPlan's world is Y-up and right-handed: X to the right, Y up,
 * Z toward the viewer. A plan is that world seen from above, so the two plan
 * axes are world X and world Z, and world Y — the only axis that carries
 * height — is dropped entirely. A viewer standing above the model looking down
 * has screen-right = +X and screen-*down* = +Z, so `PlanPoint.y` is world Z
 * with no sign flip: SVG's y-grows-downward and the plan's z-grows-toward-you
 * are the same direction. Flipping it would mirror the whole drawing, which is
 * the kind of error that does not throw and does not look wrong until somebody
 * builds from it.
 *
 * THE TRANSFORMS. Column-major 4x4, as `roomplan.ts` documents and
 * `ScanViewer` relies on: column 0 is the surface's local X axis in world space
 * (along its width), column 1 its local Y (along its height), column 3 its
 * centre. So a wall's plan run is its centre plus and minus half its width
 * along the *unit* plan projection of column 0.
 *
 * LENGTHS COME FROM `widthM`, NOT FROM THE MATRIX. The direction is taken from
 * the transform and normalised; the magnitude is taken from `ScanElement.widthM`
 * — the same field the measurement tables print. A rigid transform's basis
 * vectors are unit length so the two agree anyway, but deriving the length from
 * the matrix would make the plan capable of quoting 4.19 m for a wall the table
 * calls 4.20 m the moment a capture arrives with a non-rigid transform in it.
 * The scan-viewer and the tables are the reference; this must not be a second
 * source of truth for a measurement.
 */

import type { ScanElement, ScanElementKind, ScanRoomGroup } from "./scan-elements.js";
import {
  heightAxisOf,
  isUsableTransform,
  magnitude,
  median,
  normalOf,
  translationOf,
  widthAxisOf,
  type Vec3,
} from "./spatial.js";

export interface PlanPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Where a wall, window, door or opening sits in height: world Y of its bottom
 * and top edges, metres. The plan drops height, but a few questions asked of
 * the plan need it back — "does a radiator fit under this window's sill?" —
 * so it is carried alongside rather than re-read from the matrix elsewhere.
 */
export interface PlanVerticalExtent {
  readonly bottomY: number;
  readonly topY: number;
}

/** A wall, window, door or opening as it runs across the plan, in metres. */
export interface PlanSegment {
  readonly id: string;
  readonly kind: ScanElementKind;
  readonly label: string;
  readonly roomIndex: number;
  readonly roomLabel: string;
  readonly start: PlanPoint;
  readonly end: PlanPoint;
  /** `ScanElement.widthM` verbatim — see the file header. */
  readonly lengthM: number;
  /** The opening's host wall, when RoomPlan recorded one. */
  readonly parentId?: string;
  /**
   * Bottom and top edge heights, world Y. Present only when the surface's
   * height axis (transform column 1) is within ~8° of vertical: a tilted
   * height axis would make "centre minus half the height" a guess.
   */
  readonly vertical?: PlanVerticalExtent;
  /** `ScanElement.thicknessM`: set only when the designer gave the wall one. */
  readonly thicknessM?: number;
}

/**
 * The floor of a room, as a world-Y height — or why it could not be settled.
 *
 * RoomPlan's world origin is wherever the phone was when the session began,
 * so a window's height means nothing until it is measured from the floor of
 * its room. Two independent observations of that floor are in a capture: the
 * floor surface's own centre (transform column 3, Y), and the bottom edge of
 * every wall (centre Y minus half its height). Where both exist they must
 * agree within `FLOOR_LEVEL_TOLERANCE_M` before either is believed; where
 * only the walls exist, at least two of them must agree with each other. A
 * floor that cannot be settled is reported as unknown with the reason, never
 * guessed — the question it answers (a radiator under a sill) is exactly the
 * kind where a confident wrong number costs a site visit.
 */
export type PlanFloorLevel =
  | {
      readonly known: true;
      /** World Y of the floor, metres. */
      readonly y: number;
      /** Which observations settled it. */
      readonly source: "floor-and-walls" | "floor" | "walls";
    }
  | { readonly known: false; readonly reason: string };

/** How far apart two observations of one floor may be and still be one floor. */
export const FLOOR_LEVEL_TOLERANCE_M = 0.05;

/** cos 8°: an axis at least this vertical counts as vertical. */
const VERTICAL_COS = 0.99;

export interface PlanRoom {
  readonly index: number;
  /** `ScanRoomGroup.externalRef`: the join to the survey's heat-loss room. */
  readonly externalRef?: string;
  readonly label: string;
  readonly story: number;
  readonly storyLabel: string;
  readonly floorAreaM2?: number;
  /** One closed loop per captured floor surface. Empty when no floor was seen. */
  readonly outlines: readonly (readonly PlanPoint[])[];
  /**
   * The floor element each outline came from, index for index — so a click on
   * a floor in the plan can select the same element the Floors table and the
   * 3D view select.
   */
  readonly floorIds: readonly string[];
  /** Where the room's name and area belong. */
  readonly centre: PlanPoint;
  readonly segments: readonly PlanSegment[];
  /** The room's floor height — see `PlanFloorLevel`. */
  readonly floorLevel: PlanFloorLevel;
}

export interface PlanBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

const PLACEABLE: ReadonlySet<ScanElementKind> = new Set([
  "wall",
  "window",
  "door",
  "opening",
]);

/**
 * A world-space axis flattened to plan, normalised.
 *
 * Returns `undefined` for an axis that is (near enough) vertical, because such
 * an axis has no direction in plan at all and normalising it would amplify
 * floating-point noise into a confident, wrong bearing.
 */
function planAxis(axis: Vec3): PlanPoint | undefined {
  const length = Math.hypot(axis.x, axis.z);
  if (!Number.isFinite(length) || length < 1e-6) return undefined;
  return { x: axis.x / length, y: axis.z / length };
}

function segmentOf(element: ScanElement): PlanSegment | undefined {
  const { transform } = element;
  if (!isUsableTransform(transform)) return undefined;
  const along = planAxis(widthAxisOf(transform));
  if (!along) return undefined;
  const lengthM = element.widthM;
  if (!Number.isFinite(lengthM) || lengthM <= 0) return undefined;

  const centre = translationOf(transform);
  const half = lengthM / 2;
  const vertical = verticalExtentOf(element);
  return {
    id: element.id,
    kind: element.kind,
    label: element.label,
    roomIndex: element.roomIndex,
    roomLabel: element.roomLabel,
    start: { x: centre.x - along.x * half, y: centre.z - along.y * half },
    end: { x: centre.x + along.x * half, y: centre.z + along.y * half },
    lengthM,
    ...(element.parentId ? { parentId: element.parentId } : {}),
    ...(vertical ? { vertical } : {}),
    ...(element.thicknessM !== undefined ? { thicknessM: element.thicknessM } : {}),
  };
}

/** Is this world axis (near enough) straight up or down? */
function isVertical(axis: Vec3): boolean {
  const length = magnitude(axis);
  if (!Number.isFinite(length) || length < 1e-6) return false;
  return Math.abs(axis.y) / length >= VERTICAL_COS;
}

/**
 * A standing surface's bottom and top, world Y: its centre (column 3) less and
 * plus half its height, the height running up column 1. Undefined when column
 * 1 is not vertical or the height is not a positive number.
 */
function verticalExtentOf(element: ScanElement): PlanVerticalExtent | undefined {
  const { transform } = element;
  if (!isUsableTransform(transform)) return undefined;
  if (!isVertical(heightAxisOf(transform))) return undefined;
  const height = element.heightM;
  if (!Number.isFinite(height) || !(height > 0)) return undefined;
  const centreY = translationOf(transform).y;
  return { bottomY: centreY - height / 2, topY: centreY + height / 2 };
}

const mm = (metres: number): number => Math.round(metres * 1000);

/** See `PlanFloorLevel`. */
function floorLevelOf(elements: readonly ScanElement[]): PlanFloorLevel {
  // A floor surface counts only when it lies flat: its normal (column 2) vertical.
  const floors = elements
    .filter(
      (e) =>
        e.kind === "floor" &&
        isUsableTransform(e.transform) &&
        isVertical(normalOf(e.transform)),
    )
    .map((e) => translationOf(e.transform as readonly number[]).y);
  const wallBases = elements
    .filter((e) => e.kind === "wall")
    .map(verticalExtentOf)
    .filter((v): v is PlanVerticalExtent => v !== undefined)
    .map((v) => v.bottomY);

  const spread = (values: readonly number[]): number =>
    Math.max(...values) - Math.min(...values);

  if (floors.length > 0) {
    if (spread(floors) > FLOOR_LEVEL_TOLERANCE_M) {
      return {
        known: false,
        reason: `the room's floor surfaces are ${mm(spread(floors))} mm apart in height`,
      };
    }
    const floorY = median(floors) as number;
    if (wallBases.length === 0) return { known: true, y: floorY, source: "floor" };
    const wallY = median(wallBases) as number;
    if (Math.abs(floorY - wallY) > FLOOR_LEVEL_TOLERANCE_M) {
      return {
        known: false,
        reason: `the floor and the bottom of the walls disagree by ${mm(Math.abs(floorY - wallY))} mm in this capture`,
      };
    }
    return { known: true, y: floorY, source: "floor-and-walls" };
  }

  if (wallBases.length >= 2) {
    if (spread(wallBases) > FLOOR_LEVEL_TOLERANCE_M) {
      return {
        known: false,
        reason: `no floor was captured and the walls' bottoms are ${mm(spread(wallBases))} mm apart`,
      };
    }
    return { known: true, y: median(wallBases) as number, source: "walls" };
  }
  return {
    known: false,
    reason: "no floor was captured, and there are too few walls to find it from",
  };
}

/**
 * A floor surface as the quadrilateral it covers.
 *
 * A floor's local X runs along its width and its local Y along its depth, both
 * horizontal because the surface lies flat — so both project to real plan
 * directions. When the depth axis projects to nothing (an identity transform,
 * which is what synthetic fixtures carry and what a floor whose matrix was
 * never populated looks like) the outline falls back to the left-hand
 * perpendicular of the width axis: still a rectangle of the right size, just
 * axis-aligned rather than truly oriented.
 */
function outlineOf(element: ScanElement): readonly PlanPoint[] | undefined {
  // A floor the plan's edit layer redrew to corrected walls carries its own outline.
  if (element.planOutline && element.planOutline.length >= 3) return element.planOutline;
  const { transform } = element;
  if (!isUsableTransform(transform)) return undefined;
  const along = planAxis(widthAxisOf(transform));
  if (!along) return undefined;
  const across = planAxis(heightAxisOf(transform)) ?? { x: -along.y, y: along.x };

  const width = element.widthM;
  const depth = element.heightM;
  if (!(width > 0) || !(depth > 0)) return undefined;

  const centre = translationOf(transform);
  const c: PlanPoint = { x: centre.x, y: centre.z };
  const hw = width / 2;
  const hd = depth / 2;
  const corner = (sw: number, sd: number): PlanPoint => ({
    x: c.x + along.x * hw * sw + across.x * hd * sd,
    y: c.y + along.y * hw * sw + across.y * hd * sd,
  });
  return [corner(-1, -1), corner(1, -1), corner(1, 1), corner(-1, 1)];
}

function centreOf(
  outlines: readonly (readonly PlanPoint[])[],
  segments: readonly PlanSegment[],
): PlanPoint {
  const points: PlanPoint[] = [];
  for (const outline of outlines) points.push(...outline);
  if (points.length === 0) {
    for (const segment of segments) {
      points.push({
        x: (segment.start.x + segment.end.x) / 2,
        y: (segment.start.y + segment.end.y) / 2,
      });
    }
  }
  if (points.length === 0) return { x: 0, y: 0 };
  const sum = points.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 },
  );
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/**
 * Every room of a scan, projected to plan.
 *
 * Elements the projection cannot place — no transform, a width axis that points
 * straight up, a zero width — are dropped rather than guessed at. A wall drawn
 * in the wrong place is worse than a wall not drawn, because only one of those
 * two is visible as a problem.
 */
export function scanPlanRooms(
  groups: readonly ScanRoomGroup[],
): readonly PlanRoom[] {
  return groups.map((group) => {
    const segments: PlanSegment[] = [];
    const outlines: (readonly PlanPoint[])[] = [];
    const floorIds: string[] = [];

    for (const element of group.elements) {
      if (PLACEABLE.has(element.kind)) {
        const segment = segmentOf(element);
        if (segment) segments.push(segment);
      } else if (element.kind === "floor") {
        const outline = outlineOf(element);
        if (outline) {
          outlines.push(outline);
          floorIds.push(element.id);
        }
      }
    }

    return {
      index: group.index,
      ...(group.externalRef !== undefined ? { externalRef: group.externalRef } : {}),
      label: group.label,
      story: group.story,
      storyLabel: group.storyLabel,
      ...(typeof group.floorAreaM2 === "number"
        ? { floorAreaM2: group.floorAreaM2 }
        : {}),
      outlines,
      floorIds,
      centre: centreOf(outlines, segments),
      segments,
      floorLevel: floorLevelOf(group.elements),
    };
  });
}

/** The extent of what will be drawn, in metres. `undefined` when nothing will. */
export function planBounds(
  rooms: readonly PlanRoom[],
): PlanBounds | undefined {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const see = (p: PlanPoint): void => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  };

  for (const room of rooms) {
    for (const outline of room.outlines) for (const p of outline) see(p);
    for (const segment of room.segments) {
      see(segment.start);
      see(segment.end);
    }
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return undefined;
  return { minX, minY, maxX, maxY };
}

/** Every distinct corner of the plan — what a hand-placed dimension snaps to. */
export function planVertices(rooms: readonly PlanRoom[]): readonly PlanPoint[] {
  const seen = new Map<string, PlanPoint>();
  const keep = (p: PlanPoint): void => {
    // Millimetre buckets: two walls meeting at a corner are the same vertex to
    // a surveyor even when the capture puts them 0.4 mm apart.
    const key = `${Math.round(p.x * 1000)}:${Math.round(p.y * 1000)}`;
    if (!seen.has(key)) seen.set(key, p);
  };
  for (const room of rooms) {
    for (const outline of room.outlines) for (const p of outline) keep(p);
    for (const segment of room.segments) {
      keep(segment.start);
      keep(segment.end);
    }
  }
  return [...seen.values()];
}

/** The storeys present, in order, for a plan that shows one at a time. */
export function planStories(
  rooms: readonly PlanRoom[],
): readonly { readonly story: number; readonly label: string }[] {
  const seen = new Map<number, string>();
  for (const room of rooms) {
    if (!seen.has(room.story)) seen.set(room.story, room.storyLabel);
  }
  return [...seen.entries()]
    .sort(([a], [b]) => a - b)
    .map(([story, label]) => ({ story, label }));
}

/** Straight-line distance between two plan points, in metres. */
export function planDistanceM(a: PlanPoint, b: PlanPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * The nearest vertex to a point, if one is within `radiusM`.
 *
 * Callers work in screen pixels and convert — `radiusPx / pixelsPerMetre` —
 * so the snap feels the same size on screen whatever the drawing is zoomed to,
 * which is what "within N screen px" means to the hand holding the mouse.
 */
export function nearestVertex(
  point: PlanPoint,
  vertices: readonly PlanPoint[],
  radiusM: number,
): PlanPoint | undefined {
  let best: PlanPoint | undefined;
  let bestDistance = radiusM;
  for (const vertex of vertices) {
    const d = planDistanceM(point, vertex);
    // `<=` so a click exactly on the radius still snaps, and a click exactly on
    // a vertex (d = 0) snaps even when radiusM is 0.
    if (d <= bestDistance) {
      bestDistance = d;
      best = vertex;
    }
  }
  return best;
}

/** The closest point to `point` on the segment from `a` to `b`. */
export function closestPointOnSegment(
  point: PlanPoint,
  a: PlanPoint,
  b: PlanPoint,
): PlanPoint {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return a;
  const t = Math.max(
    0,
    Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared),
  );
  return { x: a.x + dx * t, y: a.y + dy * t };
}

export type PlanSnapKind = "corner" | "wall" | "free";

export interface PlanSnap {
  readonly point: PlanPoint;
  readonly kind: PlanSnapKind;
}

/**
 * Where a hand-placed dimension end actually lands.
 *
 * Two tiers, in order:
 *
 * 1. CORNER — the nearest plan vertex (wall end, floor corner, the edge of a
 *    window or door) within `radiusM`. Corners win outright, even over a wall
 *    that is nearer, because "corner to corner" is what a surveyor means nine
 *    times out of ten and a snap that slides a few millimetres along the wall
 *    instead would quietly make the figure wrong.
 * 2. WALL — failing a corner, the nearest point on any wall's run within
 *    `radiusM`, so "from this wall to that one" is measurable without having to
 *    hit an end.
 *
 * Otherwise the point is used exactly where it was clicked.
 */
export function snapPlanPoint(
  point: PlanPoint,
  vertices: readonly PlanPoint[],
  walls: readonly Pick<PlanSegment, "start" | "end">[],
  radiusM: number,
): PlanSnap {
  const corner = nearestVertex(point, vertices, radiusM);
  if (corner) return { point: corner, kind: "corner" };

  let best: PlanPoint | undefined;
  let bestDistance = radiusM;
  for (const wall of walls) {
    const candidate = closestPointOnSegment(point, wall.start, wall.end);
    const d = planDistanceM(point, candidate);
    if (d <= bestDistance) {
      bestDistance = d;
      best = candidate;
    }
  }
  if (best) return { point: best, kind: "wall" };
  return { point, kind: "free" };
}
